import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Search, X, Clock, ArrowRight, BookOpen } from "lucide-react";
import { ARTICLES } from "../data/mock";
import SectionHeader from "../components/common/SectionHeader";
import TechTag from "../components/common/TechTag";

export default function Articles() {
  const [activeCategory, setActiveCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");

  const categories = useMemo(() => {
    return ["All", ...Array.from(new Set(ARTICLES.map((a) => a.category)))];
  }, []);

  const filteredArticles = useMemo(() => {
    return ARTICLES.filter((article) => {
      const matchesCat = activeCategory === "All" || article.category === activeCategory;
      if (!matchesCat) return false;

      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase();
      const inTitle = article.title.toLowerCase().includes(q);
      const inExcerpt = article.excerpt.toLowerCase().includes(q);
      const inTags = article.tags?.some((t) => t.toLowerCase().includes(q));
      return inTitle || inExcerpt || inTags;
    });
  }, [activeCategory, searchQuery]);

  return (
    <section className="pt-36 sm:pt-40 pb-28 px-6 sm:px-8 lg:px-12">
      <div className="max-w-[1200px] mx-auto">
        <SectionHeader
          eyebrow="Writing & Analysis"
          title="Field"
          highlight="notes."
          meta={`${ARTICLES.length} published essays`}
        />

        <p className="text-foreground/75 text-lg sm:text-xl max-w-[65ch] -mt-6 mb-12 font-normal leading-relaxed">
          Reflections on software architecture, design token governance, applied machine learning retrieval, and the intersection of academic informatics with production engineering.
        </p>

        {/* Search & Category Filter Controls */}
        <div className="mb-12 flex flex-col md:flex-row gap-4 md:items-center justify-between pb-8 border-b border-border/60">
          
          {/* Categories */}
          <div className="flex flex-wrap items-center gap-2" role="tablist" aria-label="Filter articles by category">
            {categories.map((c) => {
              const count = c === "All" ? ARTICLES.length : ARTICLES.filter((a) => a.category === c).length;
              const isActive = activeCategory === c;
              return (
                <button
                  key={c}
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setActiveCategory(c)}
                  className={`font-mono text-[11px] tracking-[0.18em] uppercase px-3.5 py-1.5 rounded-full border transition-all ${
                    isActive
                      ? "border-primary/80 text-primary bg-primary/10 shadow-[0_0_15px_hsl(var(--accent)/0.15)]"
                      : "border-border/70 text-muted-foreground hover:text-foreground hover:border-border"
                  }`}
                >
                  {c} <span className="opacity-60 text-[10px]">({count})</span>
                </button>
              );
            })}
          </div>

          {/* Search Box */}
          <div className="relative w-full md:w-72">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search topics or tags..."
              aria-label="Search articles"
              className="w-full pl-10 pr-9 py-2 rounded-full border border-border/80 bg-card/60 text-[13.5px] text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                aria-label="Clear search"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-0.5"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* Article Cards List */}
        {filteredArticles.length > 0 ? (
          <ul className="divide-y divide-border/60 border-y border-border/60">
            {filteredArticles.map((article, i) => (
              <motion.li
                key={article.slug}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: i * 0.06 }}
              >
                <Link
                  to={`/articles/${article.slug}`}
                  className="group grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-8 items-start py-10 px-2 sm:px-4 rounded-xl hover:bg-card/40 transition-colors"
                >
                  {/* Meta Column */}
                  <div className="md:col-span-3 font-mono text-[11.5px] text-muted-foreground/80 space-y-1.5">
                    <div className="text-foreground/90 font-medium">{article.date}</div>
                    <div className="flex items-center gap-1.5 text-muted-foreground">
                      <Clock className="h-3 w-3" />
                      <span>{article.readMin} min read</span>
                    </div>
                    <span className="inline-block mt-2 font-mono text-[10px] tracking-[0.2em] uppercase px-2.5 py-0.5 rounded-full border border-primary/30 text-primary bg-primary/5">
                      {article.category}
                    </span>
                  </div>

                  {/* Content Column */}
                  <div className="md:col-span-8">
                    <h2 className="text-2xl sm:text-3xl md:text-4xl font-medium tracking-display group-hover:text-primary transition-colors leading-tight">
                      {article.title}
                    </h2>
                    <p className="mt-3 text-foreground/80 text-[15px] leading-relaxed max-w-[65ch]">
                      {article.excerpt}
                    </p>

                    {article.tags && article.tags.length > 0 && (
                      <div className="mt-4 flex flex-wrap gap-1.5">
                        {article.tags.map((t) => (
                          <TechTag key={t} label={t} />
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Arrow Indicator */}
                  <div className="hidden md:flex md:col-span-1 justify-end pt-2">
                    <span className="h-9 w-9 inline-flex items-center justify-center rounded-full border border-border/70 group-hover:border-primary group-hover:bg-primary/10 text-muted-foreground group-hover:text-primary transition-all">
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                    </span>
                  </div>
                </Link>
              </motion.li>
            ))}
          </ul>
        ) : (
          /* Empty State */
          <div className="py-20 text-center rounded-xl border border-dashed border-border/70 bg-card/20 px-6">
            <BookOpen className="h-10 w-10 text-muted-foreground mx-auto mb-4 opacity-50" />
            <h3 className="text-xl font-medium text-foreground mb-2">No matching field notes found</h3>
            <p className="text-muted-foreground text-sm max-w-md mx-auto mb-6">
              We couldn't find any articles matching "{searchQuery}" in category "{activeCategory}".
            </p>
            <button
              onClick={() => {
                setActiveCategory("All");
                setSearchQuery("");
              }}
              className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.18em] uppercase px-4 py-2 rounded-full border border-primary/70 text-primary hover:bg-primary/10 transition-colors"
            >
              Reset Filters
            </button>
          </div>
        )}

      </div>
    </section>
  );
}
